import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Image as ImageIcon, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { MediaPickerDialog } from "./MediaPickerDialog";
import { api, formatApiError } from "../../lib/api";
import { CATEGORIES } from "../../data/mockData";

// Formulaire Créer/Modifier un article — administre strictement le modèle Produit existant
// (ProductInput backend). Aucune logique métier (disponibilité, stock transactionnel, prix
// calculés, caution, Stripe) n'est reproduite ici : simple édition des données.
const EMPTY = { name: "", slug: "", category: "", description: "", price_ttc: "", caution_amount: "", owned_qty: "1", active: true, is_favorite: false, themes: "" };

const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const SLUG_RE = /^[a-z0-9-]{2,}$/;

export const ProductFormDialog = ({ open, onOpenChange, product, onSaved }) => {
  const editing = !!product;
  const [form, setForm] = useState(EMPTY);
  const [images, setImages] = useState([]);
  const [variants, setVariants] = useState([]);
  const [options, setOptions] = useState([]);
  const [optionGroups, setOptionGroups] = useState([]);
  const [urlInput, setUrlInput] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const initialRef = useRef("");

  useEffect(() => {
    if (!open) return;
    const init = product
      ? {
          form: {
            name: product.name, slug: product.slug, category: product.category,
            description: product.description || "", price_ttc: String(product.price_ttc),
            caution_amount: String(product.caution_amount ?? 0), owned_qty: String(product.owned_qty ?? 0),
            active: product.active !== false, is_favorite: !!product.is_favorite,
            themes: (product.themes || []).join(", "),
          },
          images: product.images || [],
          variants: (product.variants || []).map((v) => ({ name: v.name, owned_qty: String(v.owned_qty) })),
          options: (product.options || []).map((o) => ({ name: o.name, price_ttc: String(o.price_ttc), per_unit: !!o.per_unit })),
          optionGroups: (product.option_groups || []).map((g) => ({ name: g.name, required: !!g.required, choices: (g.choices || []).map((c) => ({ name: c.name, price_ttc: String(c.price_ttc), per_unit: !!c.per_unit })) })),
        }
      : { form: EMPTY, images: [], variants: [], options: [], optionGroups: [] };
    setForm(init.form);
    setImages(init.images);
    setVariants(init.variants);
    setOptions(init.options);
    setOptionGroups(init.optionGroups);
    setUrlInput("");
    setError("");
    setSaving(false);
    initialRef.current = JSON.stringify({ form: init.form, images: init.images, variants: init.variants, options: init.options, optionGroups: init.optionGroups });
  }, [open, product]);

  const isDirty = () => JSON.stringify({ form, images, variants, options, optionGroups }) !== initialRef.current;

  const requestClose = (value) => {
    if (!value && isDirty() && !saving) {
      if (!window.confirm("Vous avez des modifications non enregistrées. Fermer sans enregistrer ?")) return;
    }
    onOpenChange(value);
  };

  const validate = () => {
    if (form.name.trim().length < 2) return "Le nom est obligatoire (2 caractères minimum).";
    if (!SLUG_RE.test(form.slug)) return "La référence (slug) doit contenir au moins 2 caractères : lettres minuscules, chiffres et tirets uniquement.";
    if (!form.category) return "Choisissez une catégorie.";
    if (form.price_ttc === "" || Number.isNaN(Number(form.price_ttc)) || Number(form.price_ttc) < 0) return "Le prix TTC doit être un nombre positif ou nul.";
    if (form.caution_amount === "" || Number.isNaN(Number(form.caution_amount)) || Number(form.caution_amount) < 0) return "La caution doit être un nombre positif ou nul.";
    if (!Number.isInteger(Number(form.owned_qty)) || Number(form.owned_qty) < 0) return "La quantité possédée doit être un entier positif ou nul.";
    const vNames = variants.map((v) => v.name.trim()).filter(Boolean);
    if (variants.some((v) => !v.name.trim())) return "Chaque variante doit avoir un nom (sinon supprimez la ligne).";
    if (new Set(vNames).size !== vNames.length) return "Deux variantes portent le même nom.";
    if (variants.some((v) => !Number.isInteger(Number(v.owned_qty)) || Number(v.owned_qty) < 0)) return "La quantité de chaque variante doit être un entier positif ou nul.";
    const oNames = options.map((o) => o.name.trim()).filter(Boolean);
    if (options.some((o) => !o.name.trim())) return "Chaque option doit avoir un nom (sinon supprimez la ligne).";
    if (new Set(oNames).size !== oNames.length) return "Deux options portent le même nom.";
    if (options.some((o) => o.price_ttc === "" || Number.isNaN(Number(o.price_ttc)) || Number(o.price_ttc) < 0)) return "Le prix de chaque option doit être un nombre positif ou nul.";
    const gNames = optionGroups.map((g) => g.name.trim()).filter(Boolean);
    if (optionGroups.some((g) => !g.name.trim())) return "Chaque groupe d'options doit avoir un nom.";
    if (new Set(gNames).size !== gNames.length) return "Deux groupes d'options portent le même nom.";
    for (const g of optionGroups) {
      if (g.choices.length < 2) return `Le groupe « ${g.name.trim()} » doit contenir au moins 2 choix.`;
      const cNames = g.choices.map((c) => c.name.trim());
      if (cNames.some((n) => !n)) return `Chaque choix du groupe « ${g.name.trim()} » doit avoir un nom.`;
      if (new Set(cNames).size !== cNames.length) return `Deux choix portent le même nom dans « ${g.name.trim()} ».`;
      if (g.choices.some((c) => c.price_ttc === "" || Number.isNaN(Number(c.price_ttc)) || Number(c.price_ttc) < 0)) return "Le prix de chaque choix doit être un nombre positif ou nul.";
    }
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    setSaving(true);
    const payload = {
      slug: form.slug,
      name: form.name.trim(),
      category: form.category,
      description: form.description.trim() || null,
      price_ttc: Number(form.price_ttc),
      caution_amount: Number(form.caution_amount),
      owned_qty: Number(form.owned_qty),
      images,
      variants: variants.map((v, i) => ({ id: slugify(v.name) || `variante-${i + 1}`, name: v.name.trim(), owned_qty: Number(v.owned_qty) })),
      options: options.map((o, i) => ({ id: slugify(o.name) || `option-${i + 1}`, name: o.name.trim(), price_ttc: Number(o.price_ttc), per_unit: !!o.per_unit })),
      option_groups: optionGroups.map((g, gi) => ({
        id: slugify(g.name) || `groupe-${gi + 1}`,
        name: g.name.trim(),
        required: !!g.required,
        choices: g.choices.map((c, ci) => ({ id: slugify(c.name) || `choix-${ci + 1}`, name: c.name.trim(), price_ttc: Number(c.price_ttc), per_unit: !!c.per_unit })),
      })),
      active: form.active,
      is_favorite: form.is_favorite,
      themes: form.themes.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.patch(`/catalog/admin/products/${product.slug}`, payload);
      else await api.post("/catalog/admin/products", payload);
      toast.success(editing ? "Article mis à jour" : "Article créé — visible dans le catalogue selon son statut");
      initialRef.current = JSON.stringify({ form, images, variants, options, optionGroups });
      onOpenChange(false);
      onSaved();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      if (e.response?.status === 409) setError("Cette référence (slug) existe déjà — choisissez-en une autre.");
      else toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const moveImage = (i, dir) => {
    const next = [...images];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setImages(next);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={requestClose}>
        <DialogContent className="bg-cream max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-deepgreen">{editing ? `Modifier — ${product.name}` : "Ajouter un article"}</DialogTitle>
            <DialogDescription>Données du modèle article existant. La disponibilité réelle reste calculée par le moteur (possédé − engagé − indisponibilités).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="label-ae" htmlFor="pf-name">Nom</label>
                <input id="pf-name" className="input-ae" value={form.name} data-testid="product-form-name"
                  onChange={(e) => {
                    const name = e.target.value;
                    setForm((f) => ({ ...f, name, slug: editing ? f.slug : slugify(name) }));
                  }} />
              </div>
              <div>
                <label className="label-ae" htmlFor="pf-slug">Référence (slug)</label>
                <input id="pf-slug" className="input-ae font-mono text-xs" value={form.slug} disabled={editing} data-testid="product-form-slug"
                  onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} />
                {editing && <p className="mt-1 text-[11px] text-muted-foreground">La référence ne change pas après création (liens et historique).</p>}
              </div>
              <div>
                <label className="label-ae" htmlFor="pf-category">Catégorie</label>
                <select id="pf-category" className="input-ae" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-form-category">
                  <option value="">— Choisir —</option>
                  {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label-ae" htmlFor="pf-qty">Quantité possédée (stock)</label>
                <input id="pf-qty" type="number" min="0" step="1" className="input-ae" value={form.owned_qty} onChange={(e) => setForm({ ...form, owned_qty: e.target.value })} data-testid="product-form-qty" />
              </div>
              <div>
                <label className="label-ae" htmlFor="pf-price">Prix TTC (€ / week-end)</label>
                <input id="pf-price" type="number" min="0" step="0.01" className="input-ae" value={form.price_ttc} onChange={(e) => setForm({ ...form, price_ttc: e.target.value })} data-testid="product-form-price" />
              </div>
              <div>
                <label className="label-ae" htmlFor="pf-caution">Caution (€)</label>
                <input id="pf-caution" type="number" min="0" step="0.01" className="input-ae" value={form.caution_amount} onChange={(e) => setForm({ ...form, caution_amount: e.target.value })} data-testid="product-form-caution" />
              </div>
              <div className="sm:col-span-2">
                <label className="label-ae" htmlFor="pf-desc">Description</label>
                <textarea id="pf-desc" className="input-ae min-h-20" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-form-description" />
              </div>
              <div className="sm:col-span-2">
                <label className="label-ae" htmlFor="pf-themes">Univers déco (filtres boutique)</label>
                <input id="pf-themes" className="input-ae" placeholder="Champêtre, Terracotta, Vintage, Chic…" value={form.themes} onChange={(e) => setForm({ ...form, themes: e.target.value })} data-testid="product-form-themes" />
                <p className="mt-1 text-[11px] text-muted-foreground">Séparez les univers par des virgules — ils alimentent les filtres de la boutique.</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm font-semibold text-deepgreen">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-[#2C4C3B]" data-testid="product-form-active" />
                Actif (visible dans la boutique)
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold text-deepgreen">
                <input type="checkbox" checked={form.is_favorite} onChange={(e) => setForm({ ...form, is_favorite: e.target.checked })} className="h-4 w-4 accent-[#2C4C3B]" data-testid="product-form-favorite" />
                Coup de cœur (accueil, 4 max)
              </label>
            </div>

            {/* Images — médiathèque (plusieurs images, ordre conservé) + URL manuelle en complément */}
            <div className="rounded-xl border border-border bg-white p-4">
              <p className="label-ae">Photos (la première est l'image principale)</p>
              {images.length > 0 && (
                <div className="mt-2 grid grid-cols-3 gap-3 sm:grid-cols-4" data-testid="product-form-images">
                  {images.map((url, i) => (
                    <div key={`${url}-${i}`} className="relative rounded-lg border border-border" data-testid={`product-form-image-${i}`}>
                      <img src={url} alt={`Photo ${i + 1}`} className="aspect-square w-full rounded-lg object-cover" />
                      <div className="absolute inset-x-1 bottom-1 flex justify-center gap-1">
                        <button type="button" onClick={() => moveImage(i, -1)} disabled={i === 0} className="rounded-full bg-white/90 p-1 text-deepgreen disabled:opacity-40" data-testid={`product-form-image-up-${i}`} aria-label="Déplacer vers la gauche"><ArrowUp className="h-3 w-3" /></button>
                        <button type="button" onClick={() => moveImage(i, 1)} disabled={i === images.length - 1} className="rounded-full bg-white/90 p-1 text-deepgreen disabled:opacity-40" data-testid={`product-form-image-down-${i}`} aria-label="Déplacer vers la droite"><ArrowDown className="h-3 w-3" /></button>
                        <button type="button" onClick={() => setImages(images.filter((_, j) => j !== i))} className="rounded-full bg-white/90 p-1 text-red-700" data-testid={`product-form-image-remove-${i}`} aria-label="Retirer cette photo"><Trash2 className="h-3 w-3" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => setPickerOpen(true)} className="btn-secondary-ae !px-4 !py-2 !text-xs" data-testid="product-form-pick-media">
                  <ImageIcon className="h-4 w-4" /> Choisir depuis la Médiathèque
                </button>
                <input className="input-ae flex-1 min-w-40 !py-2 font-mono text-xs" placeholder="ou coller une URL…" value={urlInput} onChange={(e) => setUrlInput(e.target.value)} data-testid="product-form-url-input" />
                <button type="button" disabled={urlInput.trim().length < 8} onClick={() => { setImages([...images, urlInput.trim()]); setUrlInput(""); }} className="btn-secondary-ae !px-4 !py-2 !text-xs disabled:opacity-50" data-testid="product-form-url-add">
                  Ajouter l'URL
                </button>
              </div>
            </div>

            {/* Variantes du modèle existant */}
            <div className="rounded-xl border border-border bg-white p-4">
              <div className="flex items-center justify-between">
                <p className="label-ae !mb-0">Variantes (ex. coloris) — quantité possédée par variante</p>
                <button type="button" onClick={() => setVariants([...variants, { name: "", owned_qty: "1" }])} className="btn-secondary-ae !px-3 !py-1 !text-xs" data-testid="product-form-variant-add"><Plus className="h-3.5 w-3.5" /> Ajouter</button>
              </div>
              {variants.length > 0 && (
                <div className="mt-3 space-y-2" data-testid="product-form-variants">
                  {variants.map((v, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input className="input-ae flex-1 !py-2" placeholder="Nom (ex. Vert sauge)" value={v.name} onChange={(e) => setVariants(variants.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} data-testid={`product-form-variant-name-${i}`} />
                      <input type="number" min="0" step="1" className="input-ae w-24 !py-2" value={v.owned_qty} onChange={(e) => setVariants(variants.map((x, j) => (j === i ? { ...x, owned_qty: e.target.value } : x)))} data-testid={`product-form-variant-qty-${i}`} aria-label="Quantité possédée" />
                      <button type="button" onClick={() => setVariants(variants.filter((_, j) => j !== i))} className="rounded-full p-1.5 text-red-700 hover:bg-red-50" data-testid={`product-form-variant-remove-${i}`} aria-label="Supprimer la variante"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Options du modèle existant */}
            <div className="rounded-xl border border-border bg-white p-4">
              <div className="flex items-center justify-between">
                <p className="label-ae !mb-0">Options payantes (ex. montage, nettoyage)</p>
                <button type="button" onClick={() => setOptions([...options, { name: "", price_ttc: "0", per_unit: false }])} className="btn-secondary-ae !px-3 !py-1 !text-xs" data-testid="product-form-option-add"><Plus className="h-3.5 w-3.5" /> Ajouter</button>
              </div>
              {options.length > 0 && (
                <div className="mt-3 space-y-2" data-testid="product-form-options">
                  {options.map((o, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2">
                      <input className="input-ae flex-1 min-w-32 !py-2" placeholder="Nom (ex. Montage par nos soins)" value={o.name} onChange={(e) => setOptions(options.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} data-testid={`product-form-option-name-${i}`} />
                      <input type="number" min="0" step="0.01" className="input-ae w-24 !py-2" value={o.price_ttc} onChange={(e) => setOptions(options.map((x, j) => (j === i ? { ...x, price_ttc: e.target.value } : x)))} data-testid={`product-form-option-price-${i}`} aria-label="Prix TTC" />
                      <label className="flex items-center gap-1.5 text-xs font-semibold text-deepgreen">
                        <input type="checkbox" checked={o.per_unit} onChange={(e) => setOptions(options.map((x, j) => (j === i ? { ...x, per_unit: e.target.checked } : x)))} className="h-3.5 w-3.5 accent-[#2C4C3B]" data-testid={`product-form-option-perunit-${i}`} />
                        par unité
                      </label>
                      <button type="button" onClick={() => setOptions(options.filter((_, j) => j !== i))} className="rounded-full p-1.5 text-red-700 hover:bg-red-50" data-testid={`product-form-option-remove-${i}`} aria-label="Supprimer l'option"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Groupes à choix unique — cohabite avec les options plates ci-dessus */}
            <div className="rounded-xl border border-border bg-white p-4">
              <div className="flex items-center justify-between">
                <p className="label-ae !mb-0">Groupes à choix unique (ex. Voilage → une seule couleur)</p>
                <button type="button" onClick={() => setOptionGroups([...optionGroups, { name: "", required: false, choices: [{ name: "", price_ttc: "0", per_unit: false }, { name: "", price_ttc: "0", per_unit: false }] }])} className="btn-secondary-ae !px-3 !py-1 !text-xs" data-testid="product-form-group-add"><Plus className="h-3.5 w-3.5" /> Ajouter</button>
              </div>
              {optionGroups.length > 0 && (
                <div className="mt-3 space-y-3" data-testid="product-form-option-groups">
                  {optionGroups.map((g, gi) => (
                    <div key={gi} className="rounded-lg border border-sage/40 bg-cream/60 p-3" data-testid={`product-form-group-${gi}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <input className="input-ae flex-1 min-w-32 !py-2" placeholder="Nom du groupe (ex. Voilage)" value={g.name} onChange={(e) => setOptionGroups(optionGroups.map((x, j) => (j === gi ? { ...x, name: e.target.value } : x)))} data-testid={`product-form-group-name-${gi}`} />
                        <label className="flex items-center gap-1.5 text-xs font-semibold text-deepgreen">
                          <input type="checkbox" checked={g.required} onChange={(e) => setOptionGroups(optionGroups.map((x, j) => (j === gi ? { ...x, required: e.target.checked } : x)))} className="h-3.5 w-3.5 accent-[#2C4C3B]" data-testid={`product-form-group-required-${gi}`} />
                          obligatoire
                        </label>
                        <button type="button" onClick={() => setOptionGroups(optionGroups.filter((_, j) => j !== gi))} className="rounded-full p-1.5 text-red-700 hover:bg-red-50" data-testid={`product-form-group-remove-${gi}`} aria-label="Supprimer le groupe"><Trash2 className="h-4 w-4" /></button>
                      </div>
                      <div className="mt-2 space-y-2 pl-3">
                        {g.choices.map((c, ci) => (
                          <div key={ci} className="flex flex-wrap items-center gap-2">
                            <input className="input-ae flex-1 min-w-28 !py-2" placeholder="Choix (ex. Terracotta)" value={c.name} onChange={(e) => setOptionGroups(optionGroups.map((x, j) => (j === gi ? { ...x, choices: x.choices.map((y, k) => (k === ci ? { ...y, name: e.target.value } : y)) } : x)))} data-testid={`product-form-choice-name-${gi}-${ci}`} />
                            <input type="number" min="0" step="0.01" className="input-ae w-24 !py-2" value={c.price_ttc} onChange={(e) => setOptionGroups(optionGroups.map((x, j) => (j === gi ? { ...x, choices: x.choices.map((y, k) => (k === ci ? { ...y, price_ttc: e.target.value } : y)) } : x)))} data-testid={`product-form-choice-price-${gi}-${ci}`} aria-label="Supplément TTC" />
                            <label className="flex items-center gap-1.5 text-xs font-semibold text-deepgreen">
                              <input type="checkbox" checked={c.per_unit} onChange={(e) => setOptionGroups(optionGroups.map((x, j) => (j === gi ? { ...x, choices: x.choices.map((y, k) => (k === ci ? { ...y, per_unit: e.target.checked } : y)) } : x)))} className="h-3.5 w-3.5 accent-[#2C4C3B]" data-testid={`product-form-choice-perunit-${gi}-${ci}`} />
                              par unité
                            </label>
                            <button type="button" onClick={() => setOptionGroups(optionGroups.map((x, j) => (j === gi ? { ...x, choices: x.choices.filter((_, k) => k !== ci) } : x)))} className="rounded-full p-1.5 text-red-700 hover:bg-red-50" data-testid={`product-form-choice-remove-${gi}-${ci}`} aria-label="Supprimer le choix"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        ))}
                        <button type="button" onClick={() => setOptionGroups(optionGroups.map((x, j) => (j === gi ? { ...x, choices: [...x.choices, { name: "", price_ttc: "0", per_unit: false }] } : x)))} className="btn-secondary-ae !px-3 !py-1 !text-xs" data-testid={`product-form-choice-add-${gi}`}><Plus className="h-3.5 w-3.5" /> Ajouter un choix</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && <p className="rounded-lg bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-800" data-testid="product-form-error">{error}</p>}
            <div className="modal-footer-ae flex gap-3">
              <button type="button" onClick={() => requestClose(false)} className="btn-secondary-ae flex-1" data-testid="product-form-cancel">Annuler</button>
              <button type="button" onClick={submit} disabled={saving} className="btn-primary-ae flex-1 disabled:opacity-50" data-testid="product-form-submit">
                {saving ? "Enregistrement…" : editing ? "Enregistrer les modifications" : "Créer l'article"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <MediaPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} multiple onSelect={(imgs) => setImages((prev) => [...prev, ...imgs.map((m) => m.url)])} />
    </>
  );
};
